// eslint-disable-next-line import/no-cycle
import {
  numberLogicalOperators, selectionBasedIDLogicalOperators,
  selectionBasedLogicalOperators,
  stringLogicalOperators,
  dateStringLogicalOperators,
  customGeolocationLogicalOperators,
} from '@/composables/segmentations/logicalOperators';
import { fetchCustomSchemas } from '@/composables/custom-data/customData';

/**
 * Get the product custom attributes properties segments
 */
const getProductCustomAttributesPropertiesSegments = async () => {
  try {
    const customSchemas = await fetchCustomSchemas();

    // Filter schemas that have a relationship with products but NOT with customers
    const productRelatedSchemas = customSchemas.filter((schema) => {
      if (!schema.relationships || !Array.isArray(schema.relationships)) {
        return false;
      }

      // Must have a relationship with products
      const hasProductRelation = schema.relationships.some(
        (rel) => rel.targetSchemaType === 'system'
          && rel.targetSchema === 'products'
          && rel.targetField === 'product_id',
      );

      // Must NOT have a relationship with customers (those are handled in contacts)
      const hasCustomerRelation = schema.relationships.some(
        (rel) => rel.targetSchemaType === 'system' && rel.targetSchema === 'contacts',
      );

      return hasProductRelation && !hasCustomerRelation;
    });

    // Helper function to check if a schema has direct or indirect relationship with customers
    const hasIndirectCustomerRelation = (schema: any, allSchemas: any[], visited = new Set()): boolean => {
      // Prevent infinite loops in case of circular references
      if (visited.has(schema.id_custom_object)) {
        return false;
      }
      visited.add(schema.id_custom_object);

      // Check direct relationship with customers
      if (schema.relationships && Array.isArray(schema.relationships)) {
        const hasDirectCustomerRelation = schema.relationships.some(
          (rel: any) => rel.targetSchemaType === 'system' && rel.targetSchema === 'contacts',
        );

        if (hasDirectCustomerRelation) {
          return true;
        }

        // Check indirect relationships through schemas this schema points to (outgoing relations)
        const hasOutgoingIndirectRelation = schema.relationships.some((rel: any) => {
          if (rel.targetSchemaType === 'custom') {
            const targetSchema = allSchemas.find(
              (s) => s.id_custom_object.toString() === rel.targetSchema.toString(),
            );

            return targetSchema && hasIndirectCustomerRelation(targetSchema, allSchemas, new Set(visited));
          }
          return false;
        });

        if (hasOutgoingIndirectRelation) {
          return true;
        }
      }

      // Check if any other schema that has customer relation also points to this schema (incoming relations)
      const hasIncomingIndirectRelation = allSchemas.some((otherSchema) => {
        // Skip self and already visited schemas
        if (otherSchema.id_custom_object === schema.id_custom_object
            || visited.has(otherSchema.id_custom_object)) {
          return false;
        }

        if (!otherSchema.relationships || !Array.isArray(otherSchema.relationships)) {
          return false;
        }

        // Check if this other schema points to our current schema
        const pointsToCurrentSchema = otherSchema.relationships.some(
          (rel: any) => rel.targetSchemaType === 'custom'
                       && rel.targetSchema.toString() === schema.id_custom_object.toString(),
        );

        // If it points to current schema, check if it has customer relation
        if (pointsToCurrentSchema) {
          return hasIndirectCustomerRelation(otherSchema, allSchemas, new Set(visited));
        }

        return false;
      });

      return hasIncomingIndirectRelation;
    };

    // Helper function to create field properties recursively
    const createFieldProperties = async (field: any, sourceSchema: any, allSchemas: any[]) => {
      // Check if this field has a relationship with another schema
      const relationship = sourceSchema.relationships?.find(
        (rel: any) => rel.sourceField === field.name,
      );

      // If no relationship, create a simple field property
      if (!relationship) {
        // Determine logical operators based on field type
        let logicalOperators = {};

        switch (field.type) {
          case 'text':
            logicalOperators = stringLogicalOperators(`custom_${sourceSchema.id_custom_object}_${field.name}`);
            break;
          case 'number':
            logicalOperators = numberLogicalOperators(`custom_${sourceSchema.id_custom_object}_${field.name}`);
            break;
          case 'date':
            logicalOperators = dateStringLogicalOperators(`custom_${sourceSchema.id_custom_object}_${field.name}`);
            break;
          case 'boolean':
            logicalOperators = selectionBasedLogicalOperators(
              `custom_${sourceSchema.id_custom_object}_${field.name}`,
              [
                { label: 'Oui', value: true },
                { label: 'Non', value: false },
              ],
            );
            break;
          case 'geolocation':
            logicalOperators = customGeolocationLogicalOperators(`custom_${sourceSchema.id_custom_object}_${field.name}`, 'small');
            break;
          default:
            // Default to string operators if type is unknown
            logicalOperators = stringLogicalOperators(`custom_${sourceSchema.id_custom_object}_${field.name}`);
            break;
        }

        return {
          id: `custom_${sourceSchema.id_custom_object}_${field.name}`,
          label: field.label || field.name,
          logical_operators: logicalOperators,
          isCustomAttribute: true,
        };
      }

      // Handle custom schema relationships
      if (relationship.targetSchemaType === 'custom') {
        // Find the target schema
        const targetSchema = allSchemas.find(
          (schema) => schema.id_custom_object.toString() === relationship.targetSchema.toString(),
        );

        // If target schema not found, return simple field
        if (!targetSchema) {
          return {
            id: `custom_${sourceSchema.id_custom_object}_${field.name}`,
            label: field.label || field.name,
            logical_operators: stringLogicalOperators(`custom_${sourceSchema.id_custom_object}_${field.name}`),
            isCustomAttribute: true,
          };
        }

        // Check if target schema has direct or indirect relationship with customers
        const hasCustomerRelation = hasIndirectCustomerRelation(targetSchema, allSchemas);
        // If target schema is related to customers (directly or indirectly), exclude this field
        if (hasCustomerRelation) {
          return null; // This will be filtered out later
        }

        // Create sub-properties for the target schema fields
        const subProperties = await Promise.all(
          targetSchema.fields.map((targetField: any) => createFieldProperties(targetField, targetSchema, allSchemas)),
        );

        // Filter out null properties
        const validSubProperties = subProperties.filter((prop) => prop !== null);

        // Return a complex segment
        return {
          id: `custom_${sourceSchema.id_custom_object}_${field.name}`,
          label: `${field.name} (${targetSchema.schema_name})`,
          sub_properties: validSubProperties,
          isCustomAttribute: true,
        };
      }

      // For system relationships or unknown types, create a simple field
      return {
        id: `custom_${sourceSchema.id_custom_object}_${field.name}`,
        label: field.label || field.name,
        logical_operators: stringLogicalOperators(`custom_${sourceSchema.id_custom_object}_${field.name}`),
        isCustomAttribute: true,
      };
    };

    // Create segments for each schema
    const segmentsList = await Promise.all(
      productRelatedSchemas.map(async (schema) => {
        if (!schema.relationships || !Array.isArray(schema.relationships)) {
          return [];
        }
        // Find the field name used for product relationship to exclude it
        const productRelationFields = schema.relationships
          .filter((rel: any) => rel.targetSchemaType === 'system' && rel.targetSchema === 'products')
          .map((rel: any) => rel.sourceField);

        // Create sub-properties for each field in the schema, excluding product relation fields
        const subProperties = await Promise.all(
          schema.fields
            .filter((field: any) => !productRelationFields.includes(field.name))
            .map((field: any) => createFieldProperties(field, schema, customSchemas)),
        );

        // Filter out null properties (fields excluded due to indirect customer relationships)
        const validSubProperties = subProperties.filter((prop) => prop !== null);

        // If no valid properties remain, don't include this schema
        if (validSubProperties.length === 0) {
          return null;
        }

        // Return segment for this schema
        return {
          id: `custom_${schema.id_custom_object}`,
          label: schema.schema_name,
          sub_properties: validSubProperties,
          isCustomAttribute: true,
        };
      }),
    );

    // Filter out null schemas (those with no valid properties)
    return segmentsList.filter((schema) => schema !== null);
  } catch (error) {
    console.error('Error fetching product custom attributes:', error);
    return [];
  }
};

/**
 * Get the product categories properties segments
 * @param contextPrefix - Optional prefix for context path
 */
export const getProductCategoriesPropertiesSegments = async (contextPrefix?: string) => [
  {
    id: 'id_category',
    // eslint-disable-next-line max-len
    label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.categories.subProperties.idCategory',
    logical_operators: selectionBasedIDLogicalOperators('id_category'),
    ...(contextPrefix && { context: `${contextPrefix}.id_category` }),
  },
  {
    id: 'name',
    // eslint-disable-next-line max-len
    label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.categories.subProperties.name',
    logical_operators: stringLogicalOperators('name'),
    ...(contextPrefix && { context: `${contextPrefix}.name` }),
  },
];

/**
 * Get the product manufacturers properties segments
 * @param contextPrefix - Optional prefix for context path
 */
export const getProductManufacturersPropertiesSegments = async (contextPrefix?: string) => [
  {
    id: 'id_manufacturer',
    // eslint-disable-next-line max-len
    label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.manufacturers.subProperties.idManufacturer',
    logical_operators: selectionBasedIDLogicalOperators('id_manufacturer'),
    ...(contextPrefix && { context: `${contextPrefix}.id_manufacturer` }),
  },
  {
    id: 'name',
    // eslint-disable-next-line max-len
    label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.manufacturers.subProperties.name',
    logical_operators: stringLogicalOperators('name'),
    ...(contextPrefix && { context: `${contextPrefix}.name` }),
  },
];

/**
 * Get the product combinations properties segments
 * @param contextPrefix - Optional prefix for context path
 */
export const getProductCombinationsPropertiesSegments = async (contextPrefix?: string) => [
  {
    id: 'id_combination',
    label: 'segmentations.products.combinations.id_combination',
    logical_operators: selectionBasedIDLogicalOperators('id_combination'),
    ...(contextPrefix && { context: `${contextPrefix}.id_combination` }),
  },
  {
    id: 'combination_name',
    label: 'segmentations.products.combinations.name',
    logical_operators: stringLogicalOperators('combination_name'),
    ...(contextPrefix && { context: `${contextPrefix}.combination_name` }),
  },
  {
    id: 'price',
    label: 'segmentations.products.combinations.price',
    logical_operators: numberLogicalOperators('price'),
    hasCurrency: true,
    ...(contextPrefix && { context: `${contextPrefix}.price` }),
  },
  {
    id: 'price_discount',
    label: 'segmentations.products.combinations.price_discount',
    logical_operators: numberLogicalOperators('price_discount'),
    hasCurrency: true,
    ...(contextPrefix && { context: `${contextPrefix}.price_discount` }),
  },
];

/**
 * Get the product properties segments
 */
export const getProductPropertiesSegments = async () => {
  const productCategories = await getProductCategoriesPropertiesSegments();
  const productManufacturers = await getProductManufacturersPropertiesSegments();
  const productCombinations = await getProductCombinationsPropertiesSegments();
  const productCustomAttributes = await getProductCustomAttributesPropertiesSegments();
  const result: any = [
    {
      id: 'name',
      // eslint-disable-next-line max-len
      label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.productName',
      logical_operators: stringLogicalOperators('name'),
    },
    {
      id: 'id_product',
      // eslint-disable-next-line max-len
      label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.idProduct',
      logical_operators: selectionBasedIDLogicalOperators('id_product'),
    },
    {
      id: 'price',
      // eslint-disable-next-line max-len
      label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.price',
      logical_operators: numberLogicalOperators('price'),
      hasCurrency: true,
    },
    {
      id: 'price_discount',
      // eslint-disable-next-line max-len
      label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.priceDiscount',
      logical_operators: numberLogicalOperators('price_discount'),
      hasCurrency: true,
    },
    {
      id: 'link',
      // eslint-disable-next-line max-len
      label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.url',
      logical_operators: stringLogicalOperators('link'),
    },
    {
      id: 'quantity',
      // eslint-disable-next-line max-len
      label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.quantity',
      logical_operators: numberLogicalOperators('quantity'),
    },
    {
      id: 'products_categories',
      // eslint-disable-next-line max-len
      label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.categories.title',
      sub_properties: productCategories,
    },
    {
      id: 'manufacturers',
      // eslint-disable-next-line max-len
      label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.manufacturers.title',
      sub_properties: productManufacturers,
    },
    {
      id: 'products_combinations',
      label: 'segmentations.products.combinations.title',
      sub_properties: productCombinations,
    },
  ];

  if (productCustomAttributes.length > 0) {
    return result.concat(productCustomAttributes);
  }

  return result;
};

/**
 * Get the product orders properties segments
 * @param contextPrefix - Optional prefix for context path (e.g., 'cart.products')
 */
export const getProductOrdersPropertiesSegments = async (contextPrefix?: string) => {
  const categoriesPrefix = contextPrefix ? `${contextPrefix}.categories` : undefined;
  const manufacturersPrefix = contextPrefix ? `${contextPrefix}.manufacturers` : undefined;
  const combinationsPrefix = contextPrefix ? `${contextPrefix}.combinations` : undefined;

  const productCategories = await getProductCategoriesPropertiesSegments(categoriesPrefix);
  const productManufacturers = await getProductManufacturersPropertiesSegments(manufacturersPrefix);
  const productCombinations = await getProductCombinationsPropertiesSegments(combinationsPrefix);
  const productCustomAttributes = await getProductCustomAttributesPropertiesSegments();

  const result: any = [
    {
      id: 'name',
      // eslint-disable-next-line max-len
      label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.productName',
      logical_operators: stringLogicalOperators('name'),
      ...(contextPrefix && { context: `${contextPrefix}.name` }),
    },
    {
      id: 'id_product',
      // eslint-disable-next-line max-len
      label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.idProduct',
      logical_operators: selectionBasedIDLogicalOperators('id_product'),
      ...(contextPrefix && { context: `${contextPrefix}.id_product` }),
    },
    {
      id: 'price',
      // eslint-disable-next-line max-len
      label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.price',
      logical_operators: numberLogicalOperators('price'),
      hasCurrency: true,
      ...(contextPrefix && { context: `${contextPrefix}.price` }),
    },
    // {
    //   id: 'price_discount',
    //   label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.priceDiscount',
    //   logical_operators: numberLogicalOperators('price_discount'),
    //   hasCurrency: true,
    // },
    {
      id: 'qty',
      // eslint-disable-next-line max-len
      label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.quantity',
      logical_operators: numberLogicalOperators('qty'),
      ...(contextPrefix && { context: `${contextPrefix}.qty` }),
    },
    {
      id: 'products_categories',
      // eslint-disable-next-line max-len
      label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.categories.title',
      sub_properties: productCategories,
      multiple: true,
    },
    {
      id: 'manufacturers',
      // eslint-disable-next-line max-len
      label: 'segmentation.theme.contacts.groupProperties.hasDone.list.placedOrder.subProperties.orderProperties.subProperties.productsInOrder.subProperties.manufacturers.title',
      sub_properties: productManufacturers,
    },
    {
      id: 'products_combinations',
      label: 'segmentations.products.combinations.title',
      sub_properties: productCombinations,
    },
  ];

  if (productCustomAttributes.length > 0) {
    return result.concat(productCustomAttributes);
  }

  return result;
};
